/**
 * @fileoverview Interactive Transfer Function (TF) Editor
 * @module MikhmelViewer/TFEditor
 *
 * [v3.0 FIXES]:
 *  - events.on/off: bound-ссылки сохранены для корректной отписки
 *  - Конструктор: try/catch вокруг storageGet
 *  - _onColorDblClick: убран двойной colorPicker.click()
 *  - _drawHistogram: группировка пикселей (1 fillRect на сегмент вместо 650)
 *  - _drawHistogram: очистка при отсутствии данных
 *  - _promptSaveAs: .trim() защита от пробелов
 *  - _hexToRgb: поддержка #fff (3-значный hex)
 *  - console.log → log.debug
 *  - _onHistogramReady: сохранение bound-ссылки в this._histogramHandler
 *
 * Позволяет визуально настраивать:
 *  - Opacity кривую (точки HU → alpha, drag-and-drop)
 *  - Color gradient (точки HU → RGB, color picker)
 *  - Gradient opacity (edge detection)
 *  - Shading параметры (ambient, diffuse, specular)
 *  - Сохранение / загрузка / экспорт / импорт кастомных пресетов
 */

import { PRESETS_3D, PRESET_ALIAS, listAllPresets } from './presets3d.js';
import { get as storageGet, set as storageSet, KEYS } from './storage.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast, спроситьТекст } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

// ════════════════════════════════════════════════════════════════
//  1. CONSTANTS & CONFIGURATION
// ════════════════════════════════════════════════════════════════

const CONFIG = Object.freeze({
  DPR: Math.min(window.devicePixelRatio || 1, 2),
  CANVAS_W: 720,
  CANVAS_OPACITY_H: 280,
  CANVAS_COLOR_H: 30,
  CANVAS_GRADIENT_H: 100,
  CANVAS_HISTOGRAM_H: 60,
  PAD: { top: 15, right: 20, bottom: 35, left: 50 },
  COLORS: {
    bg: '#06060e',
    grid: 'rgba(255,255,255,0.04)',
    axis: 'rgba(255,255,255,0.15)',
    textDim: 'rgba(255,255,255,0.3)',
    opacityLine: '#06b6d4',
    gradientLine: '#8b5cf6',
  },
  MAX_UNDO_STEPS: 50,
  IS_TOUCH: window.matchMedia('(pointer: coarse)').matches,
});

const HIT_RADIUS = CONFIG.IS_TOUCH ? 14 : 7;
const HIT_RADIUS_HOVER = CONFIG.IS_TOUCH ? 18 : 9;

// ════════════════════════════════════════════════════════════════
//  2. STATE & HISTORY MANAGERS
// ════════════════════════════════════════════════════════════════

class HistoryManager {
  constructor() {
    this.undoStack = [];
    this.redoStack = [];
  }

  push(stateSnapshot) {
    if (this.undoStack.length > 0 && this.undoStack[this.undoStack.length - 1] === stateSnapshot) return;
    this.undoStack.push(stateSnapshot);
    if (this.undoStack.length > CONFIG.MAX_UNDO_STEPS) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  undo(currentState) {
    if (this.undoStack.length === 0) return null;
    this.redoStack.push(currentState);
    return this.undoStack.pop();
  }

  redo(currentState) {
    if (this.redoStack.length === 0) return null;
    this.undoStack.push(currentState);
    return this.redoStack.pop();
  }

  clear() {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
  }
}

class PresetStorage {
  static loadUserPresets() {
    try {
      const data = storageGet(KEYS.TF_PRESETS, {});
      return typeof data === 'object' ? data : {};
    } catch (e) {
      log.warn('[TFEditor] Failed to load user presets:', e);
      return {};
    }
  }

  static saveUserPresets(presets) {
    storageSet(KEYS.TF_PRESETS, presets);
  }
}

// ════════════════════════════════════════════════════════════════
//  3. MAIN EDITOR ENGINE
// ════════════════════════════════════════════════════════════════

class TFEditorEngine {
  constructor() {
    this._initialized = false;
    this._visible = false;
    this._liveMode = false;
    this._applyCallback = null;
    this._listeners = [];
    this._renderPending = false;

    // Bound-ссылки для внешних событий (сохраняем для отписки)
    this._histogramHandler = null;

    // Controllers
    this.history = new HistoryManager();

    // Защита конструктора: storageGet может упасть в private mode
    try {
      this.userPresets = PresetStorage.loadUserPresets();
    } catch (e) {
      log.warn('[TFEditor] Failed to load user presets in constructor:', e);
      this.userPresets = {};
    }

    // Data State
    this.state = this._getDefaultState();

    // DOM Refs
    this.refs = {};
  }

  _getDefaultState() {
    return {
      opacityPoints: [], colorPoints: [], gradientPoints: null,
      shade: true, ambient: 0.20, diffuse: 0.80, specular: 0.20, specularPower: 10,
      huMin: -1100, huMax: 3100,
      dragIndex: -1, dragType: null,
      activePresetKey: null,
      histogram: null,
    };
  }

  // ─── PUBLIC API ──────────────────────────────────────────────────

  init(applyCallback) {
    if (this._initialized) return;
    this._applyCallback = applyCallback;
    try {
      log.debug('[TFEditor] Starting init...');
      this._injectStyles();
      log.debug('[TFEditor] Styles injected');
      this._buildDOM();
      log.debug('[TFEditor] DOM built, root:', !!this.refs.root);
      this._bindEvents();
      log.debug('[TFEditor] Events bound');

      // Подписка на внешнюю гистограмму (сохраняем bound-ссылку)
      this._histogramHandler = this._onHistogramReady.bind(this);
      events.on(EVENTS.HISTOGRAM_READY, this._histogramHandler);

      this._initialized = true;
      log.debug('[TFEditor] Initialized');
    } catch (e) {
      log.error('[TFEditor] Init failed:', e);
      this._initialized = false;
    }
  }

  toggle() {
    if (!this._initialized) {
      try { this.init(this._applyCallback); } catch (e) { log.error('[TFEditor] Auto-init failed:', e); }
      if (!this._initialized) { showToast('❌ Редактор передаточной функции: не запустился', 'error', 2000); return; }
    }
    this._visible = !this._visible;
    const root = this.refs.root;
    if (!root) return;

    if (this._visible) {
      root.setAttribute('style',
        'position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:999999;' +
        'background:rgba(0,0,0,0.88);display:flex;align-items:center;justify-content:center;'
      );
      root.classList.add('open');
      const modal = root.querySelector('.tfe-modal');
      if (modal) {
        modal.setAttribute('style',
          'width:820px;max-width:95vw;max-height:92vh;overflow-y:auto;' +
          'background:#0d1520;border:1px solid rgba(0,170,255,0.4);border-radius:12px;color:#fff;' +
          'box-shadow:0 10px 40px rgba(0,0,0,0.8);display:block;'
        );
      }
      showToast('🎨 Редактор передаточной функции открыт', 'info', 1500);
      this.scheduleRender(true);
      this._updatePresetList();
    } else {
      root.setAttribute('style', 'display:none;');
      root.classList.remove('open');
    }
  }

  openWithPreset(presetKey) {
    if (!this._initialized) return;
    const resolved = PRESET_ALIAS[presetKey] || presetKey;
    const preset = PRESETS_3D[resolved] || this.userPresets[presetKey];

    if (preset) {
      this._loadPresetIntoState(preset, presetKey);
    } else {
      this._loadPresetIntoState(PRESETS_3D['ct-bone-clean'], 'ct-bone-clean');
    }

    this._visible = true;
    this.refs.root.classList.add('open');
    this.scheduleRender(true);
    this._updatePresetList();
  }

  setHistogramData(data, min, max) {
    this._onHistogramReady({ data, min, max });
  }

  destroy() {
    if (!this._initialized) return;

    // Отписка от DOM событий
    this._listeners.forEach(({ el, ev, fn, opts }) => {
      if (el) el.removeEventListener(ev, fn, opts);
    });
    this._listeners = [];

    // Отписка от глобальных событий (с правильной bound-ссылкой)
    if (this._histogramHandler) {
      events.off(EVENTS.HISTOGRAM_READY, this._histogramHandler);
      this._histogramHandler = null;
    }

    if (this.refs.root) this.refs.root.remove();
    this.history.clear();
    this.state = this._getDefaultState();
    this._initialized = false;
  }

  // ─── RENDER PIPELINE (rAF) ────────────────────────────────────────

  scheduleRender(forceSync = false) {
    if (forceSync) {
      this._renderAllCanvases();
      this._updateTables();
      this._updateUI();
      return;
    }

    if (!this._renderPending) {
      this._renderPending = true;
      requestAnimationFrame(() => {
        this._renderAllCanvases();
        this._updateTables();
        this._updateUI();
        this._renderPending = false;
      });
    }
  }

  _renderAllCanvases() {
    if (!this._visible) return;
    this._drawHistogram();
    this._drawOpacity();
    this._drawColorBar();
    this._drawGradient();
  }

  // ─── MATH & MAPPING ──────────────────────────────────────────────

  _getGraphW() { return CONFIG.CANVAS_W - CONFIG.PAD.left - CONFIG.PAD.right; }
  _getGraphH() { return CONFIG.CANVAS_OPACITY_H - CONFIG.PAD.top - CONFIG.PAD.bottom; }

  _huToX(hu) { return CONFIG.PAD.left + ((hu - this.state.huMin) / (this.state.huMax - this.state.huMin)) * this._getGraphW(); }
  _xToHU(x) { return this.state.huMin + ((x - CONFIG.PAD.left) / this._getGraphW()) * (this.state.huMax - this.state.huMin); }
  _alphaToY(a) { return CONFIG.PAD.top + (1 - a) * this._getGraphH(); }
  _yToAlpha(y) { return Math.max(0, Math.min(1, 1 - (y - CONFIG.PAD.top) / this._getGraphH())); }

  _gradMagToX(mag) { return CONFIG.PAD.left + (mag / 300) * this._getGraphW(); }
  _gradXToMag(x) { return Math.max(0, ((x - CONFIG.PAD.left) / this._getGraphW()) * 300); }
  _gradAlphaToY(a) { return 10 + (1 - a) * 70; }
  _gradYToAlpha(y) { return Math.max(0, Math.min(1, 1 - (y - 10) / 70)); }

  _getEventPos(canvas, e) {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width) / CONFIG.DPR,
      y: (e.clientY - rect.top) * (canvas.height / rect.height) / CONFIG.DPR,
    };
  }

  // ─── DRAWING ROUTINES ────────────────────────────────────────────

  _drawOpacity() {
    const ctx = this.refs.opCtx;
    const { CANVAS_W: w, CANVAS_OPACITY_H: h, PAD, COLORS } = CONFIG;
    const { huMin, huMax, opacityPoints: pts, dragType, dragIndex } = this.state;
    const gw = this._getGraphW();
    const gh = this._getGraphH();

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, w, h);

    // Сетка
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let hu = Math.ceil(huMin / 500) * 500; hu <= huMax; hu += 500) {
      const x = this._huToX(hu);
      ctx.moveTo(x, PAD.top); ctx.lineTo(x, PAD.top + gh);
    }
    for (let a = 0; a <= 1; a += 0.2) {
      const y = this._alphaToY(a);
      ctx.moveTo(PAD.left, y); ctx.lineTo(PAD.left + gw, y);
    }
    ctx.stroke();

    // Оси
    ctx.strokeStyle = COLORS.axis;
    ctx.beginPath();
    ctx.moveTo(PAD.left, PAD.top); ctx.lineTo(PAD.left, PAD.top + gh); ctx.lineTo(PAD.left + gw, PAD.top + gh);
    ctx.stroke();

    // Подписи
    ctx.fillStyle = COLORS.textDim;
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    for (let hu = Math.ceil(huMin / 500) * 500; hu <= huMax; hu += 500) {
      ctx.fillText(hu.toString(), this._huToX(hu), PAD.top + gh + 14);
    }
    ctx.textAlign = 'right';
    for (let a = 0; a <= 1; a += 0.2) {
      ctx.fillText(a.toFixed(1), PAD.left - 6, this._alphaToY(a) + 3);
    }

    if (pts.length < 2) return;

    // Заливка
    ctx.beginPath();
    ctx.moveTo(this._huToX(pts[0].hu), PAD.top + gh);
    pts.forEach(p => ctx.lineTo(this._huToX(p.hu), this._alphaToY(p.alpha)));
    ctx.lineTo(this._huToX(pts[pts.length - 1].hu), PAD.top + gh);
    ctx.closePath();

    const grad = ctx.createLinearGradient(0, PAD.top, 0, PAD.top + gh);
    grad.addColorStop(0, 'rgba(6,182,212,0.25)');
    grad.addColorStop(1, 'rgba(6,182,212,0.02)');
    ctx.fillStyle = grad;
    ctx.fill();

    // Линия
    ctx.beginPath();
    ctx.moveTo(this._huToX(pts[0].hu), this._alphaToY(pts[0].alpha));
    for (let i = 1; i < pts.length; i++) ctx.lineTo(this._huToX(pts[i].hu), this._alphaToY(pts[i].alpha));
    ctx.strokeStyle = COLORS.opacityLine;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Точки
    pts.forEach((p, i) => {
      const x = this._huToX(p.hu), y = this._alphaToY(p.alpha);
      const isActive = dragType === 'opacity' && dragIndex === i;

      ctx.beginPath();
      ctx.arc(x, y, isActive ? HIT_RADIUS_HOVER : HIT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? '#fff' : COLORS.opacityLine;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = isActive ? 2 : 1;
      ctx.stroke();

      if (isActive || pts.length <= 12) {
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(p.hu)} / ${p.alpha.toFixed(2)}`, x, y - 12);
      }
    });
  }

  _drawColorBar() {
    const ctx = this.refs.cbarCtx;
    const { CANVAS_W: w, CANVAS_COLOR_H: barH, PAD, COLORS } = CONFIG;
    const pts = this.state.colorPoints;

    ctx.clearRect(0, 0, w, barH + 30);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, w, barH + 30);

    if (pts.length < 2) return;

    const barY = 6;
    const grad = ctx.createLinearGradient(PAD.left, 0, PAD.left + this._getGraphW(), 0);
    pts.forEach(p => {
      const t = (p.hu - this.state.huMin) / (this.state.huMax - this.state.huMin);
      if (t >= 0 && t <= 1) grad.addColorStop(t, `rgb(${Math.round(p.r * 255)},${Math.round(p.g * 255)},${Math.round(p.b * 255)})`);
    });

    ctx.fillStyle = grad;
    ctx.fillRect(PAD.left, barY, this._getGraphW(), barH);
    ctx.strokeStyle = COLORS.axis;
    ctx.strokeRect(PAD.left, barY, this._getGraphW(), barH);

    // Маркеры
    pts.forEach(p => {
      const x = this._huToX(p.hu);
      const r = Math.round(p.r * 255), g = Math.round(p.g * 255), b = Math.round(p.b * 255);

      ctx.strokeStyle = `rgb(${r},${g},${b})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, barY + barH); ctx.lineTo(x, barY + barH + 10);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(x, barY + barH + 16, 6, 0, Math.PI * 2);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '7px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(Math.round(p.hu).toString(), x, barY + barH + 28);
    });
  }

  _drawGradient() {
    const ctx = this.refs.gradCtx;
    const { CANVAS_W: w, CANVAS_GRADIENT_H: h, PAD, COLORS } = CONFIG;
    const pts = this.state.gradientPoints;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, w, h);

    if (!pts) return;

    // Оси
    ctx.strokeStyle = COLORS.axis;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD.left, 10); ctx.lineTo(PAD.left, 80); ctx.lineTo(PAD.left + this._getGraphW(), 80);
    ctx.stroke();

    // Подписи
    ctx.fillStyle = COLORS.textDim;
    ctx.font = '8px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('0', PAD.left, 92);
    ctx.fillText('300', PAD.left + this._getGraphW(), 92);
    ctx.textAlign = 'right';
    ctx.fillText('1.0', PAD.left - 4, 14);
    ctx.fillText('0', PAD.left - 4, 82);

    // Заливка и Линия
    ctx.beginPath();
    ctx.moveTo(this._gradMagToX(pts[0].mag), 80);
    pts.forEach(p => ctx.lineTo(this._gradMagToX(p.mag), this._gradAlphaToY(p.alpha)));
    ctx.lineTo(this._gradMagToX(pts[pts.length - 1].mag), 80);
    ctx.closePath();
    ctx.fillStyle = 'rgba(139,92,246,0.15)';
    ctx.fill();

    ctx.beginPath();
    pts.forEach((p, i) => ctx[i === 0 ? 'moveTo' : 'lineTo'](this._gradMagToX(p.mag), this._gradAlphaToY(p.alpha)));
    ctx.strokeStyle = COLORS.gradientLine;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Точки
    pts.forEach(p => {
      ctx.beginPath();
      ctx.arc(this._gradMagToX(p.mag), this._gradAlphaToY(p.alpha), 5, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.gradientLine;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();
    });
  }

  /**
   * Отрисовка гистограммы с группировкой пикселей.
   * Вместо 650 вызовов fillRect — группируем соседние пиксели с одинаковой высотой.
   */
  _drawHistogram() {
    const ctx = this.refs.histCtx;
    if (!ctx) return;
    const w = CONFIG.CANVAS_W, h = CONFIG.CANVAS_HISTOGRAM_H;

    // Всегда очищаем — даже без данных
    ctx.clearRect(0, 0, w, h);

    if (!this.state.histogram) return;

    const { data, min, max } = this.state.histogram;
    if (!data?.length) return;

    ctx.fillStyle = CONFIG.COLORS.bg;
    ctx.fillRect(0, 0, w, h);

    let maxCount = 0;
    const huRange = max - min;
    for (let i = 0; i < data.length; i++) {
      if (min + (i / data.length) * huRange > -900 && data[i] > maxCount) maxCount = data[i];
    }

    if (maxCount === 0) return;
    const logMax = Math.log(maxCount + 1);
    const gw = this._getGraphW();
    const startX = CONFIG.PAD.left;

    // Группировка: рисуем одним fillRect для непрерывных пикселей с одинаковой высотой
    let runStart = startX;
    let runHeight = -1;

    for (let px = startX; px < startX + gw; px++) {
      const hu = this._xToHU(px);
      const binIdx = Math.floor(((hu - min) / huRange) * data.length);

      let barH = 0;
      if (binIdx >= 0 && binIdx < data.length && data[binIdx] > 0) {
        barH = Math.round((Math.log(data[binIdx] + 1) / logMax) * (h - 4));
      }

      if (barH !== runHeight) {
        // Завершаем предыдущий run
        if (runHeight > 0) {
          const c = this._interpolateColor(this._xToHU(runStart));
          ctx.fillStyle = `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},0.6)`;
          ctx.fillRect(runStart, h - runHeight, px - runStart, runHeight);
        }
        runStart = px;
        runHeight = barH;
      }
    }

    // Последний run
    if (runHeight > 0) {
      const c = this._interpolateColor(this._xToHU(runStart));
      ctx.fillStyle = `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},0.6)`;
      ctx.fillRect(runStart, h - runHeight, startX + gw - runStart, runHeight);
    }

    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.font = '8px monospace';
    ctx.textAlign = 'left';
    ctx.fillText('HU Histogram', CONFIG.PAD.left + 4, 12);
  }

  // ─── EVENT HANDLERS (Opacity) ────────────────────────────────────

  _findOpacityPoint(x, y) {
    let minDist = Infinity, idx = -1;
    this.state.opacityPoints.forEach((p, i) => {
      const d = Math.hypot(x - this._huToX(p.hu), y - this._alphaToY(p.alpha));
      if (d < minDist) { minDist = d; idx = i; }
    });
    return minDist < HIT_RADIUS * 2.5 ? idx : -1;
  }

  _onOpacityPtrDown(e) {
    if (e.button !== 0) return;
    try { this.refs.opCanvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }

    const { x, y } = this._getEventPos(this.refs.opCanvas, e);
    const idx = this._findOpacityPoint(x, y);

    if (idx >= 0) {
      this._saveSnapshot();
      this.state.dragType = 'opacity';
      this.state.dragIndex = idx;
      this.refs.opCanvas.style.cursor = 'grabbing';
    }
  }

  _onOpacityPtrMove(e) {
    const { x, y } = this._getEventPos(this.refs.opCanvas, e);

    if (this.state.dragType === 'opacity' && this.state.dragIndex >= 0) {
      const pts = this.state.opacityPoints;
      const i = this.state.dragIndex;

      let newHU = Math.round(this._xToHU(x));
      if (i > 0) newHU = Math.max(pts[i - 1].hu + 1, newHU);
      if (i < pts.length - 1) newHU = Math.min(pts[i + 1].hu - 1, newHU);

      pts[i].hu = newHU;
      pts[i].alpha = Math.round(this._yToAlpha(y) * 100) / 100;

      this.scheduleRender();
      if (this._liveMode) this._applyToViewport();
    } else {
      const idx = this._findOpacityPoint(x, y);
      this.refs.opCanvas.style.cursor = idx >= 0 ? 'grab' : 'crosshair';
    }
  }

  _onOpacityPtrUp() {
    if (this.state.dragType === 'opacity') {
      this.state.dragType = null;
      this.state.dragIndex = -1;
      this.refs.opCanvas.style.cursor = 'crosshair';
      this.scheduleRender();
    }
  }

  _onOpacityDblClick(e) {
    e.preventDefault();
    this._saveSnapshot();
    const { x, y } = this._getEventPos(this.refs.opCanvas, e);
    const hu = Math.round(this._xToHU(x));
    const alpha = Math.round(this._yToAlpha(y) * 100) / 100;

    this.state.opacityPoints.push({ hu, alpha });
    this.state.opacityPoints.sort((a, b) => a.hu - b.hu);

    this.scheduleRender(true);
    if (this._liveMode) this._applyToViewport();
    showToast(`+ Прозрачность: HU=${hu}, α=${alpha}`);
  }

  _onOpacityRightClick(e) {
    e.preventDefault();
    const { x, y } = this._getEventPos(this.refs.opCanvas, e);
    const idx = this._findOpacityPoint(x, y);
    if (idx >= 0 && this.state.opacityPoints.length > 2) {
      this._saveSnapshot();
      const p = this.state.opacityPoints[idx];
      this.state.opacityPoints.splice(idx, 1);
      this.scheduleRender(true);
      if (this._liveMode) this._applyToViewport();
      showToast(`— Прозрачность: HU=${p.hu} удалена`);
    }
  }

  // ─── EVENT HANDLERS (Color) ──────────────────────────────────────

  _findColorPoint(x) {
    let minDist = Infinity, idx = -1;
    this.state.colorPoints.forEach((p, i) => {
      const d = Math.abs(x - this._huToX(p.hu));
      if (d < minDist) { minDist = d; idx = i; }
    });
    return minDist < 15 ? idx : -1;
  }

  _onColorClick(e) {
    if (e.detail > 1) return;
    const { x } = this._getEventPos(this.refs.cbarCanvas, e);
    const idx = this._findColorPoint(x);
    if (idx >= 0) {
      const p = this.state.colorPoints[idx];
      this.refs.colorPicker.value = this._rgbToHex(p.r, p.g, p.b);
      // Снимаем старый обработчик присваиванием null
      this.refs.colorPicker.onchange = null;
      this.refs.colorPicker.onchange = () => {
        this._saveSnapshot();
        const { r, g, b } = this._hexToRgb(this.refs.colorPicker.value);
        Object.assign(this.state.colorPoints[idx], { r, g, b });
        this.scheduleRender(true);
        if (this._liveMode) this._applyToViewport();
      };
      this.refs.colorPicker.click();
    }
  }

  _onColorDblClick(e) {
    e.preventDefault();
    this._saveSnapshot();
    const { x } = this._getEventPos(this.refs.cbarCanvas, e);
    const hu = Math.round(this._xToHU(x));
    const c = this._interpolateColor(hu);

    this.state.colorPoints.push({ hu, r: c.r, g: c.g, b: c.b });
    this.state.colorPoints.sort((a, b) => a.hu - b.hu);

    const idx = this.state.colorPoints.findIndex(p => p.hu === hu);
    this.refs.colorPicker.value = this._rgbToHex(c.r, c.g, c.b);
    // Снимаем старый обработчик
    this.refs.colorPicker.onchange = null;
    this.refs.colorPicker.onchange = () => {
      const { r, g, b } = this._hexToRgb(this.refs.colorPicker.value);
      Object.assign(this.state.colorPoints[idx], { r, g, b });
      this.scheduleRender(true);
      if (this._liveMode) this._applyToViewport();
    };
    this.refs.colorPicker.click();

    this.scheduleRender(true);
    showToast(`+ Цвет: HU=${hu}`);
  }

  _onColorRightClick(e) {
    e.preventDefault();
    const { x } = this._getEventPos(this.refs.cbarCanvas, e);
    const idx = this._findColorPoint(x);
    if (idx >= 0 && this.state.colorPoints.length > 2) {
      this._saveSnapshot();
      const p = this.state.colorPoints[idx];
      this.state.colorPoints.splice(idx, 1);
      this.scheduleRender(true);
      if (this._liveMode) this._applyToViewport();
      showToast(`— Цвет: HU=${p.hu} удалена`);
    }
  }

  // ─── EVENT HANDLERS (Gradient) ───────────────────────────────────

  _findGradPoint(x, y) {
    if (!this.state.gradientPoints) return -1;
    let minDist = Infinity, idx = -1;
    this.state.gradientPoints.forEach((p, i) => {
      const d = Math.hypot(x - this._gradMagToX(p.mag), y - this._gradAlphaToY(p.alpha));
      if (d < minDist) { minDist = d; idx = i; }
    });
    return minDist < HIT_RADIUS * 2.5 ? idx : -1;
  }

  _onGradPtrDown(e) {
    if (e.button !== 0 || !this.state.gradientPoints) return;
    try { this.refs.gradCanvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }

    const { x, y } = this._getEventPos(this.refs.gradCanvas, e);
    const idx = this._findGradPoint(x, y);
    if (idx >= 0) {
      this._saveSnapshot();
      this.state.dragType = 'gradient';
      this.state.dragIndex = idx;
    }
  }

  _onGradPtrMove(e) {
    if (this.state.dragType !== 'gradient' || this.state.dragIndex < 0) return;
    const { x, y } = this._getEventPos(this.refs.gradCanvas, e);
    const pts = this.state.gradientPoints;
    const i = this.state.dragIndex;

    let mag = Math.round(this._gradXToMag(x));
    if (i > 0) mag = Math.max(pts[i - 1].mag + 1, mag);
    if (i < pts.length - 1) mag = Math.min(pts[i + 1].mag - 1, mag);

    pts[i].mag = Math.max(0, mag);
    pts[i].alpha = Math.round(this._gradYToAlpha(y) * 100) / 100;

    this.scheduleRender();
    if (this._liveMode) this._applyToViewport();
  }

  _onGradPtrUp() {
    if (this.state.dragType === 'gradient') {
      this.state.dragType = null;
      this.state.dragIndex = -1;
      this.scheduleRender();
    }
  }

  _onGradDblClick(e) {
    e.preventDefault();
    if (!this.state.gradientPoints) return;
    this._saveSnapshot();
    const { x, y } = this._getEventPos(this.refs.gradCanvas, e);
    const mag = Math.round(this._gradXToMag(x));
    const alpha = Math.round(this._gradYToAlpha(y) * 100) / 100;

    this.state.gradientPoints.push({ mag, alpha });
    this.state.gradientPoints.sort((a, b) => a.mag - b.mag);
    this.scheduleRender(true);
    if (this._liveMode) this._applyToViewport();
  }

  _onGradRightClick(e) {
    e.preventDefault();
    if (!this.state.gradientPoints) return;
    const { x, y } = this._getEventPos(this.refs.gradCanvas, e);
    const idx = this._findGradPoint(x, y);
    if (idx >= 0 && this.state.gradientPoints.length > 2) {
      this._saveSnapshot();
      this.state.gradientPoints.splice(idx, 1);
      this.scheduleRender(true);
      if (this._liveMode) this._applyToViewport();
    }
  }

  // ─── PRESETS MANAGEMENT ──────────────────────────────────────────

  _loadPresetIntoState(preset, key) {
    this.state.activePresetKey = key;
    this.state.opacityPoints = (preset.opacity || []).map(([hu, alpha]) => ({ hu, alpha }));
    this.state.colorPoints = (preset.color || []).map(([hu, r, g, b]) => ({ hu, r, g, b }));
    this.state.gradientPoints = preset.gradient ? preset.gradient.map(([mag, alpha]) => ({ mag, alpha })) : null;
    this.state.shade = preset.shade ?? true;
    this.state.ambient = preset.ambient ?? 0.2;
    this.state.diffuse = preset.diffuse ?? 0.8;
    this.state.specular = preset.specular ?? 0.2;
    this.state.specularPower = preset.specularPower ?? 10;

    if (this.refs.root) {
      this.refs.root.querySelector('#tfe-gradient-toggle').classList.toggle('on', !!this.state.gradientPoints);
      this.refs.gradCanvas.style.display = this.state.gradientPoints ? '' : 'none';
      this.refs.root.querySelector('#tfe-shade-toggle').classList.toggle('on', this.state.shade);

      this.refs.ambientInput.value = Math.round(this.state.ambient * 100);
      this.refs.ambientVal.textContent = this.state.ambient.toFixed(2);
      this.refs.diffuseInput.value = Math.round(this.state.diffuse * 100);
      this.refs.diffuseVal.textContent = this.state.diffuse.toFixed(2);
      this.refs.specularInput.value = Math.round(this.state.specular * 100);
      this.refs.specularVal.textContent = this.state.specular.toFixed(2);
      this.refs.specPowerInput.value = this.state.specularPower;
      this.refs.specPowerVal.textContent = this.state.specularPower;

      if (this.refs.presetSelect) this.refs.presetSelect.value = key;
    }
  }

  _createPresetObject(name) {
    return {
      name: name || 'Custom',
      category: 'custom',
      color: this.state.colorPoints.map(p => [p.hu, p.r, p.g, p.b]),
      opacity: this.state.opacityPoints.map(p => [p.hu, p.alpha]),
      gradient: this.state.gradientPoints ? this.state.gradientPoints.map(p => [p.mag, p.alpha]) : null,
      shade: this.state.shade,
      ambient: this.state.ambient,
      diffuse: this.state.diffuse,
      specular: this.state.specular,
      specularPower: this.state.specularPower,
    };
  }

  _saveCurrentAsUserPreset(key) {
    const name = this.userPresets[key]?.name || key;
    this.userPresets[key] = this._createPresetObject(name);
    PresetStorage.saveUserPresets(this.userPresets);
    this._updatePresetList();
    showToast(`💾 Сохранён: ${name}`);
  }

  async _promptSaveAs() {   // [11.1.18] спроситьТекст асинхронна
    const name = await спроситьТекст('Название пресета:', { поУмолчанию: `My Preset ${Object.keys(this.userPresets).length + 1}` });
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    const key = 'user_' + trimmed.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_а-яё]/gi, '');
    this.userPresets[key] = this._createPresetObject(trimmed);
    this.state.activePresetKey = key;
    PresetStorage.saveUserPresets(this.userPresets);
    this._updatePresetList();
    showToast(`💾 Сохранён: ${trimmed}`);
  }

  _exportJSON() {
    const obj = this._createPresetObject(this.state.activePresetKey || 'export');
    obj._exported = new Date().toISOString();
    obj._viewer = 'Mikhmel Cornerstone4D v5';
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tf_preset_${this.state.activePresetKey || 'custom'}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('📤 Экспортировано в JSON');
  }

  _importJSON(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const obj = JSON.parse(ev.target.result);
        if (!obj.color || !obj.opacity) throw new Error('Invalid preset format');
        const name = obj.name || file.name.replace('.json', '');
        const key = 'user_' + name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_а-яё]/gi, '');
        this.userPresets[key] = obj;
        this.userPresets[key].name = name;
        this.userPresets[key].category = 'custom';
        PresetStorage.saveUserPresets(this.userPresets);
        this._loadPresetIntoState(obj, key);
        this._updatePresetList();
        this.scheduleRender(true);
        showToast(`📥 Импортирован: ${name}`);
      } catch (err) {
        showToast(`❌ Ошибка импорта: ${err.message}`, 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  // ─── UI & DOM UPDATES ────────────────────────────────────────────

  _updatePresetList() {
    if (!this.refs.root) return;
    const sel = this.refs.presetSelect;
    sel.innerHTML = '';

    const builtIn = listAllPresets();
    const categories = [...new Set(builtIn.map(p => p.category))];
    const labels = {
      bone: '🦴 Кости', skin: '🧑 Кожа', muscle: '💪 Мышцы', vascular: '🫀 Сосуды',
      lung: '🫁 Лёгкие', gi: '🔬 ЖКТ', mri: '🧠 MRI', cardiac: '❤️ Сердце',
      obstetric: '👶 Акушерство', special: '⚡ Специальные', custom: '👤 Пользовательские',
    };

    for (const cat of categories) {
      const group = document.createElement('optgroup');
      group.label = labels[cat] || cat;
      builtIn.filter(p => p.category === cat).forEach(p => {
        const opt = document.createElement('option');
        opt.value = p.key;
        opt.textContent = p.name;
        group.appendChild(opt);
      });
      sel.appendChild(group);
    }

    const userKeys = Object.keys(this.userPresets);
    if (userKeys.length > 0) {
      const group = document.createElement('optgroup');
      group.label = '👤 Мои пресеты';
      for (const key of userKeys) {
        const opt = document.createElement('option');
        opt.value = key;
        opt.textContent = `★ ${this.userPresets[key].name || key}`;
        group.appendChild(opt);
      }
      sel.appendChild(group);
    }

    if (this.state.activePresetKey) sel.value = this.state.activePresetKey;

    // Quick preset buttons
    const quickDiv = this.refs.root.querySelector('#tfe-quick-presets');
    if (!quickDiv) return;
    quickDiv.innerHTML = '';

    const quickList = [
      'ct-bone-clean', 'ct-bone-hard', 'ct-bone-warm', 'ct-skin', 'ct-vascular', 'ct-coronary',
      'ct-lung-parenchyma', 'ct-airways', 'cardiac-3d', 'mri-brain', 'ct-overview', 'ct-xray', 'ct-density-map',
      'custom-01-ct-liver', 'custom-02-ct-kidneys', 'custom-03-ct-fat', 'custom-04-ct-brain-vessels',
      'custom-05-ct-carotid', 'custom-06-ct-dental', 'custom-07-ct-fracture', 'custom-08-ct-mip-pseudo',
      'custom-09-mri-angio', 'custom-10-mri-t2-fluid', 'custom-11-ct-cardiac-valves',
      'custom-12-ct-pulmonary-vessels', 'custom-13-ct-bronchi-lumen', 'custom-14-ct-aorta-stent',
      'custom-15-ct-muscle-skeletal',
    ];

    for (const key of quickList) {
      const p = PRESETS_3D[key];
      if (!p) continue;
      const btn = document.createElement('button');
      btn.className = 'preset-chip';
      btn.textContent = p.name;
      btn.style.fontSize = '9px';
      if (key === this.state.activePresetKey) btn.classList.add('active');
      btn.addEventListener('click', async () => {
        this._loadPresetIntoState(p, key);
        this.scheduleRender(true);
        this._updatePresetList();
        if (this._liveMode) this._applyToViewport();
      });
      quickDiv.appendChild(btn);
    }

    for (const key of userKeys) {
      const p = this.userPresets[key];
      const btn = document.createElement('button');
      btn.className = 'preset-chip';
      btn.textContent = `★ ${p.name}`;
      btn.style.fontSize = '9px';
      btn.style.borderColor = 'rgba(234,179,8,0.3)';
      if (key === this.state.activePresetKey) btn.classList.add('active');
      btn.addEventListener('click', async () => {
        this._loadPresetIntoState(p, key);
        this.scheduleRender(true);
        this._updatePresetList();
        if (this._liveMode) this._applyToViewport();
      });
      quickDiv.appendChild(btn);
    }
  }

  _updateTables() {
    if (!this.refs.root) return;
    const opTable = this.refs.root.querySelector('#tfe-opacity-table');
    const clTable = this.refs.root.querySelector('#tfe-color-table');
    if (!opTable || !clTable) return;

    opTable.innerHTML = this.state.opacityPoints.map((p, i) =>
      `<div style="display:flex; gap:4px; padding:1px 0; ${i === this.state.dragIndex && this.state.dragType === 'opacity' ? 'color:var(--cyan);' : ''}">` +
      `<span style="min-width:35px;">[${i}]</span><span style="min-width:55px;">HU: ${p.hu}</span><span>α: ${p.alpha.toFixed(2)}</span></div>`
    ).join('');

    clTable.innerHTML = this.state.colorPoints.map((p, i) => {
      const hex = this._rgbToHex(p.r, p.g, p.b);
      return `<div style="display:flex; gap:4px; padding:1px 0; align-items:center;">` +
        `<span style="min-width:35px;">[${i}]</span><span style="min-width:55px;">HU: ${p.hu}</span>` +
        `<span style="width:14px; height:14px; background:${hex}; border-radius:3px; border:1px solid rgba(255,255,255,0.2); flex-shrink:0;"></span>` +
        `<span>${hex}</span></div>`;
    }).join('');
  }

  _updateUI() {
    if (this.refs.undoBtn) {
      this.refs.undoBtn.disabled = this.history.undoStack.length === 0;
      this.refs.undoBtn.style.opacity = this.history.undoStack.length === 0 ? '0.3' : '1';
    }
    if (this.refs.redoBtn) {
      this.refs.redoBtn.disabled = this.history.redoStack.length === 0;
      this.refs.redoBtn.style.opacity = this.history.redoStack.length === 0 ? '0.3' : '1';
    }
  }

  // ─── STATE HISTORY & APPLY ───────────────────────────────────────

  _saveSnapshot() {
    this.history.push(JSON.stringify({
      op: this.state.opacityPoints,
      col: this.state.colorPoints,
      grad: this.state.gradientPoints,
      sh: {
        s: this.state.shade, a: this.state.ambient, d: this.state.diffuse,
        sp: this.state.specular, pw: this.state.specularPower,
      },
    }));
    this._updateUI();
  }

  _snapshotState() {
    return JSON.stringify({
      op: this.state.opacityPoints,
      col: this.state.colorPoints,
      grad: this.state.gradientPoints,
      sh: {
        s: this.state.shade, a: this.state.ambient, d: this.state.diffuse,
        sp: this.state.specular, pw: this.state.specularPower,
      },
    });
  }

  _restoreSnapshot(json) {
    if (!json) return;
    const d = JSON.parse(json);
    this.state.opacityPoints = d.op;
    this.state.colorPoints = d.col;
    this.state.gradientPoints = d.grad;
    Object.assign(this.state, {
      shade: d.sh.s, ambient: d.sh.a, diffuse: d.sh.d,
      specular: d.sh.sp, specularPower: d.sh.pw,
    });

    this.refs.ambientInput.value = Math.round(this.state.ambient * 100);
    this.refs.ambientVal.textContent = this.state.ambient.toFixed(2);
    this.refs.diffuseInput.value = Math.round(this.state.diffuse * 100);
    this.refs.diffuseVal.textContent = this.state.diffuse.toFixed(2);
    this.refs.specularInput.value = Math.round(this.state.specular * 100);
    this.refs.specularVal.textContent = this.state.specular.toFixed(2);
    this.refs.specPowerInput.value = this.state.specularPower;
    this.refs.specPowerVal.textContent = this.state.specularPower;

    const gradToggle = this.refs.root.querySelector('#tfe-gradient-toggle');
    gradToggle.classList.toggle('on', !!this.state.gradientPoints);
    this.refs.gradCanvas.style.display = this.state.gradientPoints ? '' : 'none';

    this.scheduleRender(true);
    if (this._liveMode) this._applyToViewport();
  }

  _applyToViewport() {
    const presetObj = this._createPresetObject(this.state.activePresetKey || 'live-edit');
    if (typeof this._applyCallback === 'function') {
      this._applyCallback(presetObj);
    }
    events.emit(EVENTS.TF_APPLIED, { presetObj, presetKey: this.state.activePresetKey });
  }

  // ─── UTILS ───────────────────────────────────────────────────────

  _onHistogramReady(data) {
    if (!data) return;
    this.state.histogram = data;
    if (this.refs.histCanvas) {
      this.refs.histCanvas.style.display = '';
      this.scheduleRender();
    }
  }

  _rgbToHex(r, g, b) {
    const toHex = v => Math.round(v * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  _hexToRgb(hex) {
    // Поддержка #fff и #ffffff
    if (hex.length === 4) {
      hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    const m = hex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return { r: 1, g: 1, b: 1 };
    return {
      r: parseInt(m[1], 16) / 255,
      g: parseInt(m[2], 16) / 255,
      b: parseInt(m[3], 16) / 255,
    };
  }

  _interpolateColor(hu) {
    const pts = this.state.colorPoints;
    if (!pts.length) return { r: 1, g: 1, b: 1 };
    if (hu <= pts[0].hu) return pts[0];
    if (hu >= pts[pts.length - 1].hu) return pts[pts.length - 1];

    for (let i = 0; i < pts.length - 1; i++) {
      if (hu >= pts[i].hu && hu <= pts[i + 1].hu) {
        const t = (hu - pts[i].hu) / (pts[i + 1].hu - pts[i].hu);
        return {
          r: pts[i].r + (pts[i + 1].r - pts[i].r) * t,
          g: pts[i].g + (pts[i + 1].g - pts[i].g) * t,
          b: pts[i].b + (pts[i + 1].b - pts[i].b) * t,
        };
      }
    }
    return { r: 1, g: 1, b: 1 };
  }

  // ─── DOM CREATION & BINDING ──────────────────────────────────────

  _injectStyles() {
    if (document.getElementById('tfe-styles')) return;
    const style = document.createElement('style');
    style.id = 'tfe-styles';
    style.textContent = `
      .tfe-modal-overlay { position:fixed; inset:0; z-index:99999; background:rgba(0,0,0,0.85); display:none; align-items:center; justify-content:center; }
      .tfe-modal-overlay.open { display:flex; }
      .tfe-modal { background:#0d1520; border:1px solid rgba(0,170,255,0.4); border-radius:12px; color:#fff; font-family:-apple-system, sans-serif; box-shadow:0 10px 40px rgba(0,0,0,0.8); }
      .tfe-modal-title { padding:12px 16px; border-bottom:1px solid rgba(255,255,255,0.1); font-weight:bold; font-size:14px; }
      .tfe-close-btn { float:right; background:none; border:none; color:#888; cursor:pointer; font-size:16px; line-height:1; }
      .tfe-close-btn:hover { color:#fff; }
      .tfe-canvas { touch-action:none; width:100%; border:1px solid rgba(255,255,255,0.15); border-radius:6px; background:#06060e; display:block; }
      .tfe-ctrl-btn { background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.2); color:#fff; padding:4px 10px; border-radius:4px; cursor:pointer; font-size:11px; transition:0.2s; }
      .tfe-ctrl-btn:hover { background:rgba(255,255,255,0.2); }
      .tfe-ctrl-btn.primary { background:linear-gradient(135deg, #0af, #08f); border:none; }
      .tfe-ctrl-btn.primary:hover { filter:brightness(1.2); }
      .tfe-ctrl-btn.danger { background:rgba(239,68,68,0.2); border-color:rgba(239,68,68,0.5); color:#fca5a5; }
      .tfe-ctrl-btn.danger:hover { background:rgba(239,68,68,0.4); }
      .preset-chip { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); color:#aaa; border-radius:12px; padding:2px 8px; cursor:pointer; transition:0.2s; white-space:nowrap; }
      .preset-chip:hover { background:rgba(255,255,255,0.1); color:#fff; }
      .preset-chip.active { background:rgba(0,170,255,0.2); border-color:#0af; color:#fff; }
      .tfe-toggle { width:32px; height:18px; background:rgba(255,255,255,0.1); border-radius:9px; position:relative; cursor:pointer; transition:0.2s; border:none; flex-shrink:0; }
      .tfe-toggle::after { content:''; position:absolute; left:2px; top:2px; width:14px; height:14px; background:#888; border-radius:50%; transition:0.2s; }
      .tfe-toggle.on { background:linear-gradient(135deg, #0af, #08f); }
      .tfe-toggle.on::after { left:16px; background:#fff; }
      .tfe-row { display:flex; justify-content:space-between; align-items:center; font-size:10px; color:#888; }
    `;
    document.head.appendChild(style);
  }

  _buildDOM() {
    if (this.refs.root) return;
    const root = document.createElement('div');
    root.id = 'modal-tf-editor';
    root.className = 'tfe-modal-overlay';

    root.innerHTML = `
      <div class="tfe-modal" style="width:820px; max-width:95vw; max-height:92vh; overflow-y:auto;">
        <div class="tfe-modal-title">
          🎨 Редактор Transfer Function
          <button class="tfe-close-btn" id="tfe-close">✕</button>
        </div>
        <div style="padding:6px 12px;">
          <div style="display:flex; align-items:center; gap:6px; margin-bottom:6px;">
            <span style="font-size:10px; color:#888; text-transform:uppercase; font-weight:700;">Пресеты</span>
            <select id="tfe-preset-select" style="flex:1; font-size:11px; background:#000; color:#fff; border:1px solid #333; border-radius:4px; padding:2px;"></select>
            <button id="tfe-load-preset" class="tfe-ctrl-btn">📥 Загрузить</button>
          </div>
          <div id="tfe-quick-presets" style="display:flex; flex-wrap:wrap; gap:3px;"></div>
        </div>
        <div style="padding:2px 12px;">
          <canvas id="tfe-histogram-canvas" class="tfe-canvas" style="height:${CONFIG.CANVAS_HISTOGRAM_H}px; display:none;"></canvas>
        </div>
        <div style="padding:6px 12px;">
          <div class="tfe-row" style="margin-bottom:4px;">
            <span style="color:#0cf; font-weight:700;">Opacity (HU → Прозрачность)</span>
            <span>ЛКМ: тянуть | ПКМ: удалить | Двойной клик: добавить</span>
          </div>
          <canvas id="tfe-opacity-canvas" class="tfe-canvas" style="height:${CONFIG.CANVAS_OPACITY_H}px; cursor:crosshair;"></canvas>
        </div>
        <div style="padding:4px 12px;">
          <div class="tfe-row" style="margin-bottom:4px;">
            <span style="color:#f48; font-weight:700;">Color (HU → Цвет)</span>
            <span>Двойной клик: добавить | Клик: изменить | ПКМ: удалить</span>
          </div>
          <canvas id="tfe-color-canvas" class="tfe-canvas" style="height:${CONFIG.CANVAS_COLOR_H + 30}px; cursor:pointer;"></canvas>
          <input type="color" id="tfe-color-picker" style="position:absolute; visibility:hidden; width:0; height:0;">
        </div>
        <div style="padding:4px 12px;">
          <div class="tfe-row" style="justify-content:flex-start; gap:8px; margin-bottom:4px;">
            <span style="color:#a6f; font-weight:700;">Gradient Opacity (Edge Detection)</span>
            <button class="tfe-toggle" id="tfe-gradient-toggle"></button>
          </div>
          <canvas id="tfe-gradient-canvas" class="tfe-canvas" style="height:${CONFIG.CANVAS_GRADIENT_H}px; cursor:crosshair; display:none;"></canvas>
        </div>
        <div style="padding:6px 12px;">
          <div class="tfe-row" style="justify-content:flex-start; gap:8px; margin-bottom:6px;">
            <span style="color:#fa0; font-weight:700;">Освещение (Shading)</span>
            <button class="tfe-toggle on" id="tfe-shade-toggle"></button>
          </div>
          <div id="tfe-shading-controls" style="display:grid; grid-template-columns:1fr 1fr; gap:4px; font-size:11px;">
            <div style="display:flex; justify-content:space-between;"><span>Ambient</span> <input type="range" id="tfe-ambient" min="0" max="100" value="20" style="width:100px;"> <span id="tfe-ambient-val">0.20</span></div>
            <div style="display:flex; justify-content:space-between;"><span>Diffuse</span> <input type="range" id="tfe-diffuse" min="0" max="100" value="80" style="width:100px;"> <span id="tfe-diffuse-val">0.80</span></div>
            <div style="display:flex; justify-content:space-between;"><span>Specular</span> <input type="range" id="tfe-specular" min="0" max="100" value="20" style="width:100px;"> <span id="tfe-specular-val">0.20</span></div>
            <div style="display:flex; justify-content:space-between;"><span>Spec.Power</span> <input type="range" id="tfe-spec-power" min="1" max="128" value="10" style="width:100px;"> <span id="tfe-spec-power-val">10</span></div>
          </div>
        </div>
        <div style="padding:4px 12px;">
          <div style="display:flex; align-items:center; gap:8px; font-size:11px;">
            <span style="color:#888; font-weight:700;">Диапазон HU:</span>
            <input type="number" id="tfe-hu-min" value="-1100" style="width:70px; background:#000; color:#fff; border:1px solid #333; border-radius:4px; padding:2px 6px;">
            <span style="color:#888;">…</span>
            <input type="number" id="tfe-hu-max" value="3100" style="width:70px; background:#000; color:#fff; border:1px solid #333; border-radius:4px; padding:2px 6px;">
            <button id="tfe-hu-apply" class="tfe-ctrl-btn">Применить</button>
          </div>
        </div>
        <details style="padding:4px 12px;">
          <summary style="font-size:10px; color:#888; cursor:pointer; user-select:none;">📋 Таблица точек (числовые значения)</summary>
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:6px;">
            <div><div style="font-size:9px; color:#0cf; font-weight:700; margin-bottom:4px;">OPACITY</div><div id="tfe-opacity-table" style="max-height:150px; overflow-y:auto; font-family:monospace; font-size:10px; color:#aaa;"></div></div>
            <div><div style="font-size:9px; color:#f48; font-weight:700; margin-bottom:4px;">COLOR</div><div id="tfe-color-table" style="max-height:150px; overflow-y:auto; font-family:monospace; font-size:10px; color:#aaa;"></div></div>
          </div>
        </details>
        <div style="padding:8px 12px; display:flex; flex-wrap:wrap; gap:6px; border-top:1px solid rgba(255,255,255,0.1); margin-top:6px;">
          <button id="tfe-undo" class="tfe-ctrl-btn" title="Ctrl+Z">↩ Undo</button>
          <button id="tfe-redo" class="tfe-ctrl-btn" title="Ctrl+Y">↪ Redo</button>
          <button id="tfe-apply" class="tfe-ctrl-btn primary">▶ Применить</button>
          <button id="tfe-live" class="tfe-ctrl-btn" title="Real-time update">🔴 Live</button>
          <div style="flex:1;"></div>
          <button id="tfe-save" class="tfe-ctrl-btn">💾 Сохранить</button>
          <button id="tfe-save-as" class="tfe-ctrl-btn">💾 Как…</button>
          <button id="tfe-delete" class="tfe-ctrl-btn danger">🗑️ Удалить</button>
          <button id="tfe-export" class="tfe-ctrl-btn">📤 Экспорт</button>
          <button id="tfe-import" class="tfe-ctrl-btn">📥 Импорт</button>
          <input type="file" id="tfe-import-file" accept=".json" style="display:none;">
          <button id="tfe-reset" class="tfe-ctrl-btn danger">↩ Сброс</button>
        </div>
      </div>
    `;
    document.body.appendChild(root);

    const initCanvas = (selector, h) => {
      const cvs = root.querySelector(selector);
      cvs.width = CONFIG.CANVAS_W * CONFIG.DPR;
      cvs.height = h * CONFIG.DPR;
      const ctx = cvs.getContext('2d');
      if (CONFIG.DPR > 1) ctx.scale(CONFIG.DPR, CONFIG.DPR);
      return { cvs, ctx };
    };

    const op = initCanvas('#tfe-opacity-canvas', CONFIG.CANVAS_OPACITY_H);
    const cb = initCanvas('#tfe-color-canvas', CONFIG.CANVAS_COLOR_H + 30);
    const gr = initCanvas('#tfe-gradient-canvas', CONFIG.CANVAS_GRADIENT_H);
    const hi = initCanvas('#tfe-histogram-canvas', CONFIG.CANVAS_HISTOGRAM_H);

    this.refs = {
      root,
      opCanvas: op.cvs, opCtx: op.ctx,
      cbarCanvas: cb.cvs, cbarCtx: cb.ctx,
      gradCanvas: gr.cvs, gradCtx: gr.ctx,
      histCanvas: hi.cvs, histCtx: hi.ctx,
      colorPicker: root.querySelector('#tfe-color-picker'),
      presetSelect: root.querySelector('#tfe-preset-select'),
      undoBtn: root.querySelector('#tfe-undo'),
      redoBtn: root.querySelector('#tfe-redo'),
      ambientInput: root.querySelector('#tfe-ambient'),
      ambientVal: root.querySelector('#tfe-ambient-val'),
      diffuseInput: root.querySelector('#tfe-diffuse'),
      diffuseVal: root.querySelector('#tfe-diffuse-val'),
      specularInput: root.querySelector('#tfe-specular'),
      specularVal: root.querySelector('#tfe-specular-val'),
      specPowerInput: root.querySelector('#tfe-spec-power'),
      specPowerVal: root.querySelector('#tfe-spec-power-val'),
    };
  }

  _bindEvents() {
    const addEvt = (el, ev, fn, opts) => {
      const boundFn = fn.bind(this);
      el.addEventListener(ev, boundFn, opts);
      this._listeners.push({ el, ev, fn: boundFn, opts });
    };

    const root = this.refs.root;

    // Background click to close
    addEvt(root, 'click', (e) => { if (e.target === root) this.toggle(); });
    addEvt(root.querySelector('#tfe-close'), 'click', () => this.toggle());

    // Opacity Canvas
    addEvt(this.refs.opCanvas, 'pointerdown', this._onOpacityPtrDown);
    addEvt(this.refs.opCanvas, 'pointermove', this._onOpacityPtrMove);
    addEvt(this.refs.opCanvas, 'pointerup', this._onOpacityPtrUp);
    addEvt(this.refs.opCanvas, 'pointercancel', this._onOpacityPtrUp);
    addEvt(this.refs.opCanvas, 'pointerleave', this._onOpacityPtrUp);
    addEvt(this.refs.opCanvas, 'dblclick', this._onOpacityDblClick);
    addEvt(this.refs.opCanvas, 'contextmenu', this._onOpacityRightClick);

    // Color Canvas
    addEvt(this.refs.cbarCanvas, 'click', this._onColorClick);
    addEvt(this.refs.cbarCanvas, 'dblclick', this._onColorDblClick);
    addEvt(this.refs.cbarCanvas, 'contextmenu', this._onColorRightClick);

    // Gradient Canvas
    addEvt(this.refs.gradCanvas, 'pointerdown', this._onGradPtrDown);
    addEvt(this.refs.gradCanvas, 'pointermove', this._onGradPtrMove);
    addEvt(this.refs.gradCanvas, 'pointerup', this._onGradPtrUp);
    addEvt(this.refs.gradCanvas, 'pointercancel', this._onGradPtrUp);
    addEvt(this.refs.gradCanvas, 'pointerleave', this._onGradPtrUp);
    addEvt(this.refs.gradCanvas, 'dblclick', this._onGradDblClick);
    addEvt(this.refs.gradCanvas, 'contextmenu', this._onGradRightClick);

    // Toggles
    addEvt(root.querySelector('#tfe-gradient-toggle'), 'click', (e) => {
      e.target.classList.toggle('on');
      const on = e.target.classList.contains('on');
      this.refs.gradCanvas.style.display = on ? '' : 'none';
      if (on && !this.state.gradientPoints) {
        this.state.gradientPoints = [
          { mag: 0, alpha: 0 }, { mag: 20, alpha: 0 },
          { mag: 60, alpha: 0.5 }, { mag: 200, alpha: 1.0 },
        ];
      } else if (!on) {
        this.state.gradientPoints = null;
      }
      this.scheduleRender(true);
      if (this._liveMode) this._applyToViewport();
    });

    addEvt(root.querySelector('#tfe-shade-toggle'), 'click', (e) => {
      e.target.classList.toggle('on');
      this.state.shade = e.target.classList.contains('on');
      const ctrls = root.querySelector('#tfe-shading-controls');
      ctrls.style.opacity = this.state.shade ? '1' : '0.3';
      ctrls.style.pointerEvents = this.state.shade ? '' : 'none';
      if (this._liveMode) this._applyToViewport();
    });

    // Shading Sliders
    ['ambient', 'diffuse', 'specular'].forEach(name => {
      addEvt(this.refs[`${name}Input`], 'input', (e) => {
        this.state[name] = parseInt(e.target.value) / 100;
        this.refs[`${name}Val`].textContent = this.state[name].toFixed(2);
        if (this._liveMode) this._applyToViewport();
      });
    });
    addEvt(this.refs.specPowerInput, 'input', (e) => {
      this.state.specularPower = parseInt(e.target.value);
      this.refs.specPowerVal.textContent = this.state.specularPower;
      if (this._liveMode) this._applyToViewport();
    });

    // HU Range
    addEvt(root.querySelector('#tfe-hu-apply'), 'click', () => {
      this.state.huMin = parseInt(root.querySelector('#tfe-hu-min').value) || -1100;
      this.state.huMax = parseInt(root.querySelector('#tfe-hu-max').value) || 3100;
      this.scheduleRender(true);
    });

    // Preset Load
    addEvt(root.querySelector('#tfe-load-preset'), 'click', () => {
      const key = this.refs.presetSelect.value;
      if (key) {
        const preset = PRESETS_3D[key] || this.userPresets[key];
        if (preset) { this._loadPresetIntoState(preset, key); this.scheduleRender(true); }
      }
    });

    // Action Buttons
    addEvt(this.refs.undoBtn, 'click', () => {
      const prev = this.history.undo(this._snapshotState());
      if (prev) { this._restoreSnapshot(prev); showToast('↩ Отменить'); }
    });
    addEvt(this.refs.redoBtn, 'click', () => {
      const next = this.history.redo(this._snapshotState());
      if (next) { this._restoreSnapshot(next); showToast('↪ Повторить'); }
    });
    addEvt(root.querySelector('#tfe-apply'), 'click', () => this._applyToViewport());
    addEvt(root.querySelector('#tfe-live'), 'click', (e) => {
      this._liveMode = !this._liveMode;
      e.target.classList.toggle('primary', this._liveMode);
      e.target.textContent = this._liveMode ? '🔴 Live ON' : '🔴 Live';
    });
    addEvt(root.querySelector('#tfe-save'), 'click', () => {
      if (this.state.activePresetKey && this.userPresets[this.state.activePresetKey]) {
        this._saveCurrentAsUserPreset(this.state.activePresetKey);
      } else {
        this._promptSaveAs();
      }
    });
    addEvt(root.querySelector('#tfe-save-as'), 'click', () => this._promptSaveAs());
    addEvt(root.querySelector('#tfe-delete'), 'click', () => {
      if (this.state.activePresetKey && this.userPresets[this.state.activePresetKey]) {
        if (confirm(`Удалить пресет "${this.state.activePresetKey}"?`)) {
          delete this.userPresets[this.state.activePresetKey];
          PresetStorage.saveUserPresets(this.userPresets);
          this.state.activePresetKey = null;
          this._updatePresetList();
          showToast('🗑️ Пресет удалён');
        }
      } else {
        showToast('⚠️ Встроенные пресеты нельзя удалить', 'warning');
      }
    });
    addEvt(root.querySelector('#tfe-export'), 'click', () => this._exportJSON());
    addEvt(root.querySelector('#tfe-import'), 'click', () => root.querySelector('#tfe-import-file').click());
    addEvt(root.querySelector('#tfe-import-file'), 'change', this._importJSON);
    addEvt(root.querySelector('#tfe-reset'), 'click', () => {
      if (this.state.activePresetKey) {
        const resolved = PRESET_ALIAS[this.state.activePresetKey] || this.state.activePresetKey;
        const preset = PRESETS_3D[resolved];
        if (preset) {
          this._loadPresetIntoState(preset, this.state.activePresetKey);
          this.scheduleRender(true);
          showToast('↩ Сброс к заводскому');
        }
      }
    });

    // Keyboard Shortcuts
    addEvt(document, 'keydown', (e) => {
      if (!this._visible) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); this.refs.undoBtn.click(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); this.refs.redoBtn.click(); }
    });
  }
}

// ════════════════════════════════════════════════════════════════
//  4. SINGLETON EXPORT & BACKWARD COMPATIBILITY
// ════════════════════════════════════════════════════════════════

const tfEditorInstance = new TFEditorEngine();

export const initTFEditor = (cb) => tfEditorInstance.init(cb);
export const toggleTFEditor = () => tfEditorInstance.toggle();
export const openWithPreset = (key) => tfEditorInstance.openWithPreset(key);
export const setHistogramData = (data, min, max) => tfEditorInstance.setHistogramData(data, min, max);
export const destroyTFEditor = () => tfEditorInstance.destroy();